import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { Button, Paper, Stack, Typography } from '@mui/material';
import type { AssistantAction } from '@prospero/shared/src/assistant/assistant-contract';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { RoutesList } from '../../config/routes-list';
import { TOUCH_TARGET_PX } from '../../config/touch-target';
import { formatIsoDay } from '../../pages/consultant/deadlines/deadline-row-format';
import { buildSingleDeadlineQuery } from '../../pages/documents-archive/deadlines/deadline-filter-url';
import { DeadlineLinksViewAllButton } from './deadline-links-view-all-button';

type OpenMyDeadlinesAction = Extract<AssistantAction, { kind: 'OPEN_MY_DEADLINES' }>;

const KEY = 'guida.assistant.myDeadlines';

/** The tenant's own deadlines a reply cited, each one a jump to its card in the documents archive. */
export function MyDeadlineLinksCard({ action }: { action: OpenMyDeadlinesAction }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const title = t(`${KEY}.linksTitle`, { defaultValue: 'Le tue scadenze' });
  const untitledLabel = t(`${KEY}.untitledDeadline`, { defaultValue: 'Scadenza' });
  const undatedLabel = t(`${KEY}.undated`, { defaultValue: 'senza data' });
  return (
    <Paper aria-label={title} role="region" sx={{ borderRadius: 2, p: 2 }} variant="outlined">
      <Stack spacing={0.75}>
        <Typography sx={{ fontWeight: 700 }} variant="body2">
          {title}
        </Typography>
        {action.links.map((link) => (
          <Button
            data-testid={`my-deadline-link-${link.deadlineId}`}
            fullWidth
            key={link.deadlineId}
            onClick={() =>
              navigate(
                `${RoutesList.DOCUMENTS}?${buildSingleDeadlineQuery(link.deadlineId, link.dueAt)}`,
              )
            }
            startIcon={<EventOutlinedIcon />}
            sx={{
              justifyContent: 'flex-start',
              minHeight: TOUCH_TARGET_PX,
              textAlign: 'left',
              textTransform: 'none',
            }}
            variant="outlined"
          >
            {`${link.label.trim() || untitledLabel} · ${
              link.dueAt ? formatIsoDay(link.dueAt, i18n.language) : undatedLabel
            }`}
          </Button>
        ))}
        <DeadlineLinksViewAllButton
          shownCount={action.links.length}
          total={action.total}
          url={action.url}
        />
      </Stack>
    </Paper>
  );
}
